import { formatPrice, usePrice } from '@corratech/pylot-price'
import type { PriceRange } from '@pylot-data/pylotschema'
import cn from 'classnames'
import type { ReactElement } from 'react'
import s from '@components/common/ProductPrice/ProductPrice.module.scss'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React from 'react'

export type ProductPrice = {
    priceRange: PriceRange
    className?: string
    productType?: string
    bundlePrice?: { price: number; discountPrice: number } | null
    canUpdateBundlePrice?: boolean
    subscriptionDiscount?: number
    showDiscountPrice?: boolean
    priceText?: boolean
    is_bulk_product?: boolean
}

export const ProductPrice = ({
    priceRange,
    className = '',
    productType,
    bundlePrice,
    canUpdateBundlePrice,
    subscriptionDiscount,
    showDiscountPrice = true,
    priceText,
    is_bulk_product
}: ProductPrice): ReactElement => {
    const { locale = 'en' } = useRouter()
    const { t } = useTranslation('common')
    const { subtotal, total } = usePrice(priceRange)

    const regularValue = priceRange?.minimum_price?.regular_price?.value ?? 0
    const finalValue = priceRange?.minimum_price?.final_price?.value ?? 0
    const hasDiscount = regularValue > finalValue

    const getConfigProductPrices = (price: PriceRange) => {
        const maxPrice = price?.maximum_price?.final_price
        const minPrice = price.minimum_price.final_price
        if (minPrice?.value! < maxPrice?.value!) {
            return t('Starting at {{min}}', {
                min: formatPrice(minPrice, locale),
                max: formatPrice(maxPrice!, locale)
            })
        }
        return null
    }

    if (
        productType === 'ConfigurableProduct' ||
        productType === 'BundleProduct' ||
        productType === 'GiftCardProduct'
    ) {
        if (bundlePrice?.price && canUpdateBundlePrice) {
            return (
                <div
                    className={cn(
                        s['product-price'],
                        'product-price',
                        className
                    )}
                >
                    <span className={cn(s['product-price-value'])}>
                        {priceText && (
                            <span className={s['from-word']}>{t('From')} </span>
                        )}
                        <span
                            className={cn({
                                [s['discount-price']]:
                                    bundlePrice?.price !==
                                    bundlePrice?.discountPrice
                            })}
                        >
                            {formatPrice(
                                {
                                    value: bundlePrice?.discountPrice,
                                    currency:
                                        priceRange.minimum_price.regular_price
                                            .currency
                                },
                                locale
                            )}
                        </span>
                    </span>
                    {priceRange?.minimum_price?.regular_price?.value &&
                        bundlePrice?.price !== bundlePrice?.discountPrice &&
                        showDiscountPrice && (
                            <span
                                className={cn(
                                    s['line-through'],
                                    'line-through'
                                )}
                            >
                                {formatPrice(
                                    {
                                        value: bundlePrice?.price,
                                        currency:
                                            priceRange.minimum_price
                                                .regular_price.currency
                                    },
                                    locale
                                )}
                            </span>
                        )}
                </div>
            )
        }
        const configProductPrices = getConfigProductPrices(priceRange)
        if (priceText && !hasDiscount && configProductPrices) {
            return (
                <div
                    className={cn(
                        s['product-price'],
                        'product-price',
                        className
                    )}
                >
                    <span className={s['product-price-value']}>
                        {configProductPrices}
                    </span>
                </div>
            )
        }
    }

    const showStrike = hasDiscount && showDiscountPrice

    return (
        <div className={cn(s['product-price'], 'product-price', className)}>
            <span className={cn(s['product-price-value'])}>
                {priceText &&
                    showStrike &&
                    (productType === 'BundleProduct' ||
                        productType === 'ConfigurableProduct') && (
                        <span className={s['from-word']}>{t('From')} </span>
                    )}
                <span
                    className={cn({
                        [s['discount-price']]: showStrike,
                        'discount-price': showStrike
                    })}
                >
                    {total}
                </span>
            </span>
            {priceRange?.minimum_price?.regular_price?.value && showStrike && (
                <span className={cn(s['line-through'], 'line-through')}>
                    {subtotal}
                </span>
            )}
        </div>
    )
}
